// app/src/screens/Brands.jsx — real per-user BRANDS (handoff §9 library + §10 detail), pixel-
// close. CREATE-2: REAL now — list/detail read GET /v1/brands; "New brand" and "Edit brand" are
// real forms (createBrand / updateBrand); archive is deleteBrand behind an inline confirm. The
// server shape (one_liner / logo_colour / products[{name,desc,assets}] / voice[{label}]) is the
// old demo vocabulary made real; the screen barely changed, the data source did. Every string
// via t().

import React, { useEffect, useState } from 'react';
import { Plus, Play, Trash2, X } from 'lucide-react';
import { listBrands, getBrand, createBrand, updateBrand, deleteBrand } from '@common/drafts.js';
import { useI18n } from '../i18n/index.jsx';
import { useSession } from '../lib/session.jsx';
import { useIsMobile } from '../lib/useIsMobile.js';
import { useToast } from '../lib/toast.jsx';
import { Avatar } from '../components/ui.jsx';
import { brandLetter, brandGradient, accentAt } from '../lib/brand-visual.js';

// explicit per-bar heights + colours matching the comp waveform (Quocia Studio.dc.html line 499)
const WAVE = [[40, 'var(--border-strong)'], [70, '#FF9E93'], [100, 'var(--coral)'], [60, '#FF9E93'], [85, 'var(--coral)'], [45, 'var(--border-strong)'], [75, '#FF9E93'], [55, 'var(--border-strong)'], [95, 'var(--coral)'], [50, 'var(--border-strong)'], [65, '#FF9E93'], [35, 'var(--border-strong)']];

const fieldStyle = { padding: '11px 13px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface)', font: '500 13.5px var(--font-body)', color: 'var(--ink)', outline: 'none', width: '100%' };
const labelStyle = { font: '600 10px var(--font-mono)', color: 'var(--text-faint)', letterSpacing: '.08em', textTransform: 'uppercase' };

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span className="mono" style={labelStyle}>{label}</span>
      {children}
    </label>
  );
}

// ---- create / edit form (a full-view, like the detail) ------------------------------------
function BrandForm({ initial, onCancel, onSaved }) {
  const { t } = useI18n();
  const { api } = useSession();
  const toast = useToast();
  const editing = !!(initial && initial.id);
  const [f, setF] = useState(() => ({
    name: (initial && initial.name) || '',
    one_liner: (initial && initial.one_liner) || '',
    what: (initial && initial.what) || '',
    who: (initial && initial.who) || '',
    tone: (initial && initial.tone) || '',
    logo_colour: (initial && initial.logo_colour) || '#10C9A2',
    colours: (initial && Array.isArray(initial.colours) && initial.colours.length ? initial.colours.slice() : ['#10C9A2']),
    products: (initial && Array.isArray(initial.products) ? initial.products.map((p) => ({ name: p.name || '', desc: p.desc || '' })) : []),
    voice: (initial && Array.isArray(initial.voice) ? initial.voice.map((v) => v.label).filter(Boolean) : []),
  }));
  const [voiceInput, setVoiceInput] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF((s) => ({ ...s, ...patch }));

  async function save() {
    if (saving) return;
    if (!f.name.trim()) { toast(t('brandForm.nameRequired')); return; }
    setSaving(true);
    const body = {
      name: f.name.trim(), one_liner: f.one_liner, what: f.what, who: f.who, tone: f.tone,
      logo_colour: f.logo_colour, colours: f.colours.filter(Boolean),
      products: f.products.filter((p) => p.name.trim()).map((p) => ({ name: p.name.trim(), desc: p.desc, assets: 0 })),
      voice: f.voice.map((label) => ({ label, accent: '' })),
      ...(editing ? {} : (initial && initial.source_run_id ? { source_run_id: initial.source_run_id } : {})),
    };
    const r = editing ? await updateBrand(api, initial.id, body) : await createBrand(api, body);
    setSaving(false);
    if (r.kind === 'ok') { toast(editing ? t('brandForm.updated') : t('brandForm.created')); onSaved(r.body); }
    else { toast(r.error || (r.errors && r.errors[0]) || t('common.error')); }
  }

  return (
    <div className="view" style={{ maxWidth: 640 }}>
      <button className="q-btn" onClick={onCancel} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: '600 12px var(--font-mono)', color: 'var(--text-faint)', background: 'transparent', border: 'none', marginBottom: 14, cursor: 'pointer' }}>← {t('common.cancel')}</button>
      <h1 className="h-page" style={{ marginBottom: 18 }}>{editing ? t('brandForm.editTitle') : t('brandForm.createTitle')}</h1>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label={t('brandForm.name')}><input value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder={t('brandForm.namePlaceholder')} style={fieldStyle} /></Field>
        <Field label={t('brandForm.oneLiner')}><input value={f.one_liner} onChange={(e) => set({ one_liner: e.target.value })} placeholder={t('brandForm.oneLinerPlaceholder')} style={fieldStyle} /></Field>
        <Field label={t('brands.whatItDoes')}><textarea value={f.what} onChange={(e) => set({ what: e.target.value })} rows={2} placeholder={t('brandForm.whatPlaceholder')} style={{ ...fieldStyle, resize: 'vertical' }} /></Field>
        <Field label={t('brands.whoItsFor')}><textarea value={f.who} onChange={(e) => set({ who: e.target.value })} rows={2} placeholder={t('brandForm.whoPlaceholder')} style={{ ...fieldStyle, resize: 'vertical' }} /></Field>
        <Field label={t('brandForm.tone')}><input value={f.tone} onChange={(e) => set({ tone: e.target.value })} placeholder={t('brandForm.tonePlaceholder')} style={fieldStyle} /></Field>

        {/* colours */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span className="mono" style={labelStyle}>{t('brands.colours')}</span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {f.colours.map((c, i) => (
              <span key={i} style={{ position: 'relative', display: 'inline-flex' }}>
                <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(c) ? c : '#10C9A2'} onChange={(e) => set({ colours: f.colours.map((x, j) => (j === i ? e.target.value : x)) })} style={{ width: 42, height: 42, borderRadius: 10, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', padding: 2 }} />
                <button className="q-btn" onClick={() => set({ colours: f.colours.filter((_, j) => j !== i) })} style={{ position: 'absolute', top: -6, insetInlineEnd: -6, width: 18, height: 18, borderRadius: 999, background: 'var(--ink)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={11} strokeWidth={2.6} /></button>
              </span>
            ))}
            {f.colours.length < 8 && <button className="q-btn press" onClick={() => set({ colours: [...f.colours, '#7C5CFF'] })} style={{ width: 42, height: 42, borderRadius: 10, border: '1.5px dashed var(--border-strong)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Plus size={16} color="var(--text-faint)" strokeWidth={2.2} /></button>}
          </div>
        </div>

        {/* logo colour */}
        <Field label={t('brands.logoType')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(f.logo_colour) ? f.logo_colour : '#10C9A2'} onChange={(e) => set({ logo_colour: e.target.value })} style={{ width: 42, height: 42, borderRadius: 10, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', padding: 2 }} />
            <span className="mono" style={{ font: '500 12px var(--font-mono)', color: 'var(--text-faint)' }}>{f.logo_colour}</span>
          </div>
        </Field>

        {/* products */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span className="mono" style={labelStyle}>{t('brands.tabProducts')}</span>
          {f.products.map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input value={p.name} onChange={(e) => set({ products: f.products.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} placeholder={t('brandForm.productName')} style={{ ...fieldStyle, flex: '0 0 40%' }} />
              <input value={p.desc} onChange={(e) => set({ products: f.products.map((x, j) => (j === i ? { ...x, desc: e.target.value } : x)) })} placeholder={t('brandForm.productDesc')} style={{ ...fieldStyle, flex: 1 }} />
              <button className="q-btn" onClick={() => set({ products: f.products.filter((_, j) => j !== i) })} style={{ flex: 'none', width: 38, height: 38, borderRadius: 10, background: 'var(--fill-chip)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Trash2 size={15} color="var(--text-faint)" strokeWidth={2} /></button>
            </div>
          ))}
          <button className="q-btn press" onClick={() => set({ products: [...f.products, { name: '', desc: '' }] })} style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--fill-ghost)', color: 'var(--ink)', padding: '9px 14px', borderRadius: 999, font: '700 12px var(--font-body)', border: 'none' }}><Plus size={14} strokeWidth={2.2} />{t('brandForm.addProduct')}</button>
        </div>

        {/* voice traits */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span className="mono" style={labelStyle}>{t('brands.howItSpeaks')}</span>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {f.voice.map((v, i) => (
              <span key={i} className="mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: '600 11px var(--font-mono)', color: `var(--${accentAt(i)})`, background: `var(--${accentAt(i)}-tint)`, borderRadius: 999, padding: '5px 6px 5px 11px' }}>
                {v}<button className="q-btn" onClick={() => set({ voice: f.voice.filter((_, j) => j !== i) })} style={{ width: 16, height: 16, borderRadius: 999, background: 'transparent', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'inherit' }}><X size={11} strokeWidth={2.6} /></button>
              </span>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); const v = voiceInput.trim(); if (v) { set({ voice: [...f.voice, v] }); setVoiceInput(''); } }} style={{ display: 'flex', gap: 8 }}>
            <input value={voiceInput} onChange={(e) => setVoiceInput(e.target.value)} placeholder={t('brandForm.voicePlaceholder')} style={{ ...fieldStyle, flex: 1 }} />
            <button type="submit" className="q-btn press" style={{ background: 'var(--fill-ghost)', color: 'var(--ink)', padding: '0 16px', borderRadius: 10, font: '700 12px var(--font-body)', border: 'none' }}>{t('brandForm.addTrait')}</button>
          </form>
        </div>

        <button className="q-btn press" disabled={saving} onClick={save} style={{ background: 'var(--coral)', color: '#fff', padding: 14, borderRadius: 13, font: '700 14px var(--font-body)', boxShadow: 'var(--shadow-cta-sm)', opacity: saving ? 0.6 : 1, marginTop: 4 }}>{editing ? t('brandForm.saveEdit') : t('brandForm.saveCreate')}</button>
      </div>
    </div>
  );
}

function BrandDetail({ brand, onBack, onEdit, onArchived }) {
  const { t } = useI18n();
  const { api } = useSession();
  const mobile = useIsMobile();
  const toast = useToast();
  const [tab, setTab] = useState('about');
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const TABS = [['about', 'brands.tabAbout'], ['visual', 'brands.tabVisual'], ['products', 'brands.tabProducts'], ['voice', 'brands.tabVoice']];
  const colours = brand.colours || [];
  const products = brand.products || [];
  const voice = brand.voice || [];

  async function archive() {
    if (archiving) return;
    setArchiving(true);
    const r = await deleteBrand(api, brand.id);
    setArchiving(false);
    if (r.kind === 'ok') { toast(t('brandForm.archived')); onArchived(); }
    else { toast(r.error || t('common.error')); }
  }

  return (
    <div className="view">
      <button className="q-btn" onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: '600 12px var(--font-mono)', color: 'var(--text-faint)', background: 'transparent', border: 'none', marginBottom: 14, cursor: 'pointer' }}>← {t('brands.back')}</button>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar letter={brandLetter(brand)} gradient={brandGradient(brand)} size={56} radius={16} fontSize={22} />
          <div>
            <h1 style={{ font: '700 28px/1 var(--font-body)', letterSpacing: '-.03em', margin: '0 0 6px' }}>{brand.name}</h1>
            <p className="muted" style={{ margin: 0, fontSize: 13.5 }}>{brand.one_liner || t('brands.noOneLiner')}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="q-btn press btn-outline" onClick={onEdit}>{t('brands.edit')}</button>
          <button className="q-btn press" onClick={() => setConfirmArchive(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--fill-chip)', color: 'var(--text-muted)', border: 'none', padding: '9px 14px', borderRadius: 999, font: '700 12.5px var(--font-body)' }}><Trash2 size={14} strokeWidth={2} />{t('brands.archive')}</button>
        </div>
      </div>

      {confirmArchive && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--coral-tint)', borderRadius: 14, padding: '13px 16px', marginTop: 16, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, font: '500 13px var(--font-body)', color: 'var(--ink)' }}>{t('brands.archiveConfirm', { name: brand.name })}</span>
          <button className="q-btn press btn-ghost" onClick={() => setConfirmArchive(false)}>{t('common.cancel')}</button>
          <button className="q-btn press" disabled={archiving} onClick={archive} style={{ background: 'var(--coral)', color: '#fff', padding: '9px 16px', borderRadius: 10, font: '700 12.5px var(--font-body)', opacity: archiving ? 0.6 : 1 }}>{t('brands.archive')}</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, margin: '24px 0 20px', borderBottom: '1px solid var(--border)', overflowX: 'auto' }} className="h-scroll">
        {TABS.map(([k, key]) => (
          <button key={k} className="q-btn" onClick={() => setTab(k)} style={{ padding: '10px 16px', font: `${tab === k ? 700 : 600} 13.5px var(--font-body)`, color: tab === k ? 'var(--ink)' : 'var(--text-faint)', borderBottom: tab === k ? '2.5px solid var(--coral)' : '2.5px solid transparent', marginBottom: -1, background: 'transparent', border: 'none', cursor: 'pointer', flex: 'none' }}>{t(key)}</button>
        ))}
      </div>

      {tab === 'about' && (
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fit,minmax(232px,1fr))', gap: 16 }}>
          {[['brands.whatItDoes', 'var(--coral)', brand.what], ['brands.whoItsFor', 'var(--violet)', brand.who], ['brands.tone', 'var(--mint)', brand.tone]].map(([key, c, v]) => (
            <div key={key} className="q-card" style={{ padding: 18, borderRadius: 18, boxShadow: 'none' }}>
              <div className="mono" style={{ font: '600 10px var(--font-mono)', color: c, letterSpacing: '.08em', marginBottom: 9 }}>{t(key).toUpperCase()}</div>
              <p style={{ font: '400 14px/1.5 var(--font-body)', margin: 0, color: v ? 'var(--ink)' : 'var(--text-faint)' }}>{v || t('brands.notSet')}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'visual' && (
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 16 }}>
          <div className="q-card" style={{ padding: 20, borderRadius: 18, boxShadow: 'none' }}>
            <div className="mono" style={{ font: '600 10px var(--font-mono)', color: 'var(--text-faint)', letterSpacing: '.08em', marginBottom: 14 }}>{t('brands.colours').toUpperCase()}</div>
            {colours.length ? (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {colours.map((c, i) => (
                  <div key={i} style={{ flex: '0 0 auto', minWidth: 64 }}>
                    <div style={{ height: 56, width: 64, borderRadius: 12, background: c }} />
                    <div className="mono" style={{ font: '500 10px var(--font-mono)', color: 'var(--text-faint)', marginTop: 6 }}>{c}</div>
                  </div>
                ))}
              </div>
            ) : <p className="muted" style={{ fontSize: 13, margin: 0 }}>{t('brands.noColours')}</p>}
          </div>
          <div className="q-card" style={{ padding: 20, borderRadius: 18, boxShadow: 'none' }}>
            <div className="mono" style={{ font: '600 10px var(--font-mono)', color: 'var(--text-faint)', letterSpacing: '.08em', marginBottom: 14 }}>{t('brands.logoType').toUpperCase()}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 70, height: 70, borderRadius: 16, background: '#F5EFE4', display: 'flex', alignItems: 'center', justifyContent: 'center', font: '700 30px var(--font-body)', color: brand.logo_colour || 'var(--ink)' }}>{brandLetter(brand)}</div>
              <div>
                <div style={{ font: '700 20px var(--font-body)' }}>{brand.name}</div>
                <div className="mono" style={{ font: '400 12px var(--font-mono)', color: 'var(--text-faint)', marginTop: 3 }}>{t('brands.displayType')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'products' && (
        products.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fit,minmax(232px,1fr))', gap: 16 }}>
            {products.map((p, i) => (
              <div key={i} className="q-card" style={{ padding: 18, borderRadius: 18, boxShadow: 'none' }}>
                <div style={{ font: '700 15px var(--font-body)', marginBottom: 4 }}>{p.name}</div>
                {p.desc && <div className="muted" style={{ font: '400 12px/1.5 var(--font-body)', marginBottom: 12 }}>{p.desc}</div>}
                <span className="mono" style={{ font: '600 10px var(--font-mono)', color: 'var(--mint)', background: 'var(--mint-tint)', borderRadius: 999, padding: '4px 9px' }}>{t('brands.savedAssets', { n: Number(p.assets) || 0 })}</span>
              </div>
            ))}
          </div>
        ) : <p className="muted" style={{ fontSize: 13 }}>{t('brands.noProducts')}</p>
      )}

      {tab === 'voice' && (
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 16 }}>
          <div className="q-card" style={{ padding: 20, borderRadius: 18, boxShadow: 'none' }}>
            <div className="mono" style={{ font: '600 10px var(--font-mono)', color: 'var(--text-faint)', letterSpacing: '.08em', marginBottom: 14 }}>{t('brands.voiceSample').toUpperCase()}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button className="q-btn press" onClick={() => toast(t('brands.voiceSoon'))} style={{ width: 44, height: 44, borderRadius: 999, background: 'var(--coral)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', border: 'none' }}><Play size={17} color="#fff" fill="#fff" strokeWidth={0} /></button>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 3, height: 34 }}>
                {WAVE.map(([h, c], i) => <div key={i} style={{ width: 3, height: `${h}%`, background: c, borderRadius: 9 }} />)}
              </div>
              <span className="mono" style={{ font: '500 11px var(--font-mono)', color: 'var(--text-faint)' }}>0:08</span>
            </div>
          </div>
          <div className="q-card" style={{ padding: 20, borderRadius: 18, boxShadow: 'none' }}>
            <div className="mono" style={{ font: '600 10px var(--font-mono)', color: 'var(--text-faint)', letterSpacing: '.08em', marginBottom: 14 }}>{t('brands.howItSpeaks').toUpperCase()}</div>
            {voice.length ? (
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                {voice.map((v, i) => (
                  <span key={i} className="mono" style={{ font: '600 11px var(--font-mono)', color: `var(--${accentAt(i)})`, background: `var(--${accentAt(i)}-tint)`, borderRadius: 999, padding: '5px 11px' }}>{v.label}</span>
                ))}
              </div>
            ) : <p className="muted" style={{ fontSize: 13, margin: 0 }}>{t('brands.noVoice')}</p>}
            <p className="muted" style={{ font: '400 13px/1.5 var(--font-body)', margin: '14px 0 0' }}>{t('brands.voiceNote')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Brands() {
  const { t } = useI18n();
  const { api } = useSession();
  const mobile = useIsMobile();
  const [brands, setBrands] = useState(null);
  const [active, setActive] = useState(null);   // brand id in detail
  const [form, setForm] = useState(null);        // { initial: brand|null } when editing/creating

  const load = async (selectId) => {
    const r = await listBrands(api);
    const list = r.kind === 'ok' ? (r.body.brands || []) : [];
    setBrands(list);
    if (selectId !== undefined) setActive(selectId);
    return list;
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (form) {
    return <BrandForm initial={form.initial} onCancel={() => setForm(null)} onSaved={async (saved) => { setForm(null); await load(saved && saved.id); }} />;
  }
  if (active) {
    const brand = (brands || []).find((b) => b.id === active);
    if (!brand) { setActive(null); return null; }
    return <BrandDetail brand={brand} onBack={() => setActive(null)} onEdit={() => setForm({ initial: brand })} onArchived={async () => { setActive(null); await load(); }} />;
  }
  if (!brands) return <div className="view"><p className="muted">{t('common.loading')}</p></div>;

  return (
    <div className="view">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 20, gap: 10 }}>
        <div>
          <h1 className="h-page" style={{ marginBottom: 4 }}>{t('brands.title')}</h1>
          {!mobile && <p className="muted" style={{ margin: 0, fontSize: 14 }}>{t('brands.subtitle')}</p>}
        </div>
        <button className="q-btn press" onClick={() => setForm({ initial: null })} style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'var(--ink)', color: '#fff', padding: mobile ? '8px 14px' : '10px 17px', borderRadius: 999, font: `700 ${mobile ? 11.5 : 12.5}px var(--font-body)` }}>{!mobile && <Plus size={15} color="#fff" strokeWidth={2.2} />}{mobile ? t('brands.newShort') : t('brands.new')}</button>
      </div>

      {brands.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px' }}>
          <p className="muted" style={{ fontSize: 15, margin: '0 0 18px' }}>{t('brands.empty')}</p>
          <button className="q-btn press btn-primary" onClick={() => setForm({ initial: null })}>{t('brands.new')} →</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fit,minmax(232px,1fr))', gap: mobile ? 11 : 16 }}>
          {brands.map((b) => (
            <button key={b.id} className="q-btn qcard lift" onClick={() => setActive(b.id)} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: mobile ? 17 : 20, padding: mobile ? 16 : 20, textAlign: 'start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Avatar letter={brandLetter(b)} gradient={brandGradient(b)} size={mobile ? 42 : 46} radius={mobile ? 13 : 14} fontSize={mobile ? 17 : 18} />
                <div style={{ flex: 1 }}>
                  <div style={{ font: `700 ${mobile ? 15 : 16}px var(--font-body)` }}>{b.name}</div>
                  <div className="mono" style={{ font: `400 ${mobile ? 10 : 11}px var(--font-mono)`, color: 'var(--text-faint)', marginTop: 2 }}>{t('brands.stat', { n: (b.products || []).length })}</div>
                </div>
                {mobile && <span style={{ color: 'var(--text-divider)' }}>›</span>}
              </div>
              {!mobile && b.one_liner && <p className="muted" style={{ font: '400 13px/1.5 var(--font-body)', margin: '14px 0 0' }}>{b.one_liner}</p>}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: mobile ? 'flex-start' : 'center', gap: 11, background: 'var(--violet-tint)', borderRadius: mobile ? 14 : 16, padding: mobile ? 13 : '15px 18px', marginTop: 16 }}>
        <svg width={mobile ? 16 : 18} height={mobile ? 16 : 18} viewBox="0 0 24 24" fill="none" stroke="var(--violet)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none', marginTop: mobile ? 1 : 0 }}><path d="M12 2v4M12 18v4M2 12h4M18 12h4" /><circle cx="12" cy="12" r="4" /></svg>
        <span style={{ font: `500 ${mobile ? 12 : 13}px/1.5 var(--font-body)`, color: 'var(--violet-ink)' }}>{mobile ? t('brands.noteMobile') : t('brands.note')}</span>
      </div>
    </div>
  );
}
